import { auth } from '@jaymathew/ui-shared/apis/FirebaseUtilities/FirebaseConfig'
import { BasicDialog } from '@jaymathew/ui-shared/elements/BasicDialog'
import { Sidebar as ABSidebar, Divider, ListItem } from '@jaymathew/ui-shared/elements/Sidebar'
import { isPathDisabled } from '@jaymathew/ui-shared/utils/isPathDisabled'
import ThemeLightDarkIcon from '@mui/icons-material/Brightness4'
import ToggleSidebarIcon from '@mui/icons-material/MultipleStop'
import SignoutIcon from '@mui/icons-material/PowerSettingsNew'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Fragment, Suspense, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { showDarkTheme } from 'redux/appSettingsSlice'
import { AppState } from 'redux/reducerIndex'
import { AppDispatch, persistor } from 'redux/store'
import {
	drawerWidth, fullLogo, loginPage, logo, toolTipSidebar
} from 'resources/constants'
import { Role } from 'resources/types'
import SidebarOptions, { SidebarOptionType, companyDynaicString } from './SidebarOptions'
import { useQueryClient } from '@tanstack/react-query'
import { LS_KEY, useSelectedCompany } from 'shared/hooks/useSelectedCompany'
import { useCurrentUser } from 'shared/hooks/useCurrentUser'

export interface SidebarProps {
	drawerOpen: boolean
	toggleDrawer: () => void
}

const Sidebar = ({ drawerOpen, toggleDrawer }:SidebarProps) => {
	const dispatch = useDispatch<AppDispatch>()
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const { breakpoints } = useTheme()
	const isMobile = useMediaQuery(breakpoints.down('md'))

	const { currentUser } = useCurrentUser()
	const { currentCompany } = useSelectedCompany()
	const { darkTheme } = useSelector((state: AppState) => state.appSettings)

	// const [shouldShowCompanyCodeDialog, setShouldShowCompanyCodeDialog] = useState(false)
	// const toggleCompanyCodeDialog = () => setShouldShowCompanyCodeDialog((prevState) => !prevState)

	const [errorInfo, setErrorInfo] = useState({ title: '', message: '', shouldShow: false })

	let signingOut = false

	const signout = async () => {
		if (signingOut) return
		signingOut = true

		try {
			// 1) Firebase sign-out (broadcasts to other tabs; triggers onIdTokenChanged)
			await auth.signOut()

			// 2) App-level cleanup in parallel (adjust to your stack)
			queryClient.clear()
			localStorage.removeItem(LS_KEY)

			// 3) Persisted Redux cleanup
			persistor.pause()
			await persistor.purge()

			navigate(loginPage)
		} catch (e) {
			setErrorInfo({ title: 'Sign out failed', message: String(e), shouldShow: true })
		} finally {
			signingOut = false
		}
	}

	const navigateTo = (path: string) => {
		navigate(path)
		if (isMobile) {
			toggleDrawer()
		}
	}

	const toggleThemeHandler = () => {
		dispatch(showDarkTheme(!darkTheme))
	}

	const sideBarItems = () => {
		const safeRole = currentUser?.role ?? Role.REALTOR
		const disabledPaths = currentCompany?.disabledPagesByRole?.[safeRole] ?? []
		const companyName = currentCompany?.name ?? 'Company'
		const companyURL = currentCompany?.url
		const hasCompanyWebsite = !!companyURL

		const normalizeURL = (url: string) =>
			url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`

		const isChildVisible = (child: SidebarOptionType) =>
			child.roles.includes(safeRole) && !isPathDisabled(child.path || '', disabledPaths)

		const getTitle = (option: SidebarOptionType) =>
			option.path === companyDynaicString ? companyName : option.title

		const getPath = (option: SidebarOptionType) =>
			option.path === companyDynaicString ? normalizeURL(companyURL!) : option.path

		return SidebarOptions
			.filter((option) => {
				if (option.path === companyDynaicString) {
					return hasCompanyWebsite && option.roles.includes(safeRole)
				}
				if (option.hasChildren && option.children) {
					return option.children.some(isChildVisible)
				}
				return option.roles.includes(safeRole) && !isPathDisabled(option.path || '', disabledPaths)
			})
			.map((option) => {
				const isExternal = option.path === companyDynaicString
				const title = getTitle(option)
				const path = getPath(option)

				return (
					<Fragment key={title}>
						{option.hasDividerAbove && <Divider />}

						<ListItem
							title={title}
							icon={option.icon}
							onClick={
								isExternal
									? () => window.open(path, '_blank', 'noopener,noreferrer')
									: () => path && navigateTo(path)
							}
							hasChildren={option.hasChildren}
						>
							{option.children?.filter(isChildVisible).map((child) => (
								<ListItem
									key={child.title}
									title={child.title}
									icon={
										<Suspense fallback={<div>Loading...</div>}>
											{child.icon}
										</Suspense>
									}
									onClick={() => child.path && navigateTo(child.path)}
									nested
								/>
							))}
						</ListItem>

						{option.hasDivderBelow && <Divider />}
					</Fragment>
				)
			})
	}

	const bottomItems = () => (
		<>
			<Divider />
			<ListItem
				title='Toggle Theme'
				onClick={toggleThemeHandler}
				icon={<ThemeLightDarkIcon />}
			/>
			<ListItem
				title='Toggle Sidebar'
				onClick={toggleDrawer}
				icon={<ToggleSidebarIcon />}
			/>
			<Divider />
			<ListItem
				title='Sign out'
				onClick={signout}
				icon={<SignoutIcon />}
			/>
		</>
	)

	return (
		<>
			<ABSidebar
				toggleDrawer={toggleDrawer}
				open={drawerOpen}
				logo={drawerOpen ? fullLogo : logo}
				drawerWidth={drawerWidth}
				toolTipEnterDelay={toolTipSidebar}
				bottomChildren={bottomItems()}
				bottomHeight={27}
			>
				{sideBarItems()}
			</ABSidebar>

			{errorInfo.shouldShow && (
				<BasicDialog
					shouldShow={errorInfo.shouldShow}
					title={errorInfo.title}
					message={errorInfo.message}
					closeDialog={() => setErrorInfo({
						title: '',
						message: '',
						shouldShow: false,
					})}
				/>
			)}
		</>
	)
}

export default Sidebar
